"use client";

import { BarChart3, Workflow, FileText, Network, LayoutDashboard, Compass } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CoreCapabilities() {
  const header = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.2
  });
  const services = [{
    code: "[ 01.0 ]",
    title: "Data Analytics",
    description: "Transforming complex datasets into clear reports, meaningful insights, and reliable information that supports confident decision-making.",
    Icon: BarChart3
  }, {
    code: "[ 02.0 ]",
    title: "Data Automation",
    description: "Streamlining repetitive workflows, reports, and data processes to reduce manual work, improve efficiency, and minimise errors.",
    Icon: Workflow
  }, {
    code: "[ 03.0 ]",
    title: "Ad Hoc Reporting",
    description: "Flexible reporting solutions tailored to specific business questions, management requirements, and immediate decision-making needs.",
    Icon: FileText
  }, {
    code: "[ 04.0 ]",
    title: "System Integration",
    description: "Connecting information from different files, platforms, and systems to create consistent and accessible data processes.",
    Icon: Network
  }, {
    code: "[ 05.0 ]",
    title: "Dashboard Design",
    description: "Creating clear and intuitive dashboards that make performance indicators, trends, and complex information easier to understand.",
    Icon: LayoutDashboard
  }, {
    code: "[ 06.0 ]",
    title: "Strategic Consulting",
    description: "Providing practical guidance on data, reporting, automation, and process improvement based on your organisation’s requirements.",
    Icon: Compass
  }];
  return <section data-section-id="994" id="core-capabilities" className="text-foreground py-32 dark !opacity-[1] bg-accent">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={header.ref} className={`grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16 transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div>
            <p className="font-mono text-xs tracking-widest text-muted-foreground uppercase mb-6">[ CAPABILITIES ]</p>

            <h2 className="font-serif font-default leading-[1.05] tracking-tight text-primary-foreground" style={{
            fontSize: "clamp(2.5rem, 5vw, 4rem)"
          }}>
              Architecting intelligence,{" "}
              <em className="italic font-serif text-secondary">
                driven by data.
              </em>
            </h2>
          </div>

          <div className="flex flex-col justify-end gap-6">
            <p className="text-base lg:text-lg text-muted-foreground leading-relaxed max-w-md">
              We connect complex data, reporting requirements, and business
              processes with practical solutions that improve clarity,
              accuracy, and operational efficiency.
            </p>

            <Link to="/OurServicesPage" className="group inline-flex items-center gap-2 font-mono text-sm text-foreground hover:text-primary transition-colors w-fit">
              <span className="border-b border-foreground/30 group-hover:border-primary pb-0.5">
                Explore our services
              </span>

              <span className="transition-transform group-hover:translate-x-1">
                &rarr;
              </span>
            </Link>
          </div>
        </div>

        <div className="mt-20 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {services.map((service, index) => {
          const Icon = service.Icon;
          return <div key={service.code} data-index={index} className="group relative flex flex-col bg-card border border-border rounded-lg p-10 transition-all duration-300 hover:border-primary overflow-hidden shadow-md">
                <div className="mb-10">
                  <div className="flex h-16 w-16 items-center justify-center bg-primary-foreground/0">
                    <Icon className="lucide lucide-chart-column text-primary transition-transform duration-300 group-hover:scale-105 bg-secondary" strokeWidth={1.5} aria-hidden="true" style={{
                  width: "42px",
                  height: "42px"
                }} />
                  </div>
                </div>

                <p className="font-mono text-xs tracking-widest text-muted-foreground uppercase mb-4">
                  {service.code}
                </p>

                <h3 className="font-serif font-default text-2xl leading-tight mb-4 text-secondary">
                  {service.title}
                </h3>

                <p className="font-default text-[15px] leading-relaxed text-muted-foreground mb-8">
                  {service.description}
                </p>

                <div className="mt-auto pt-6 border-t border-border">
                  <Link to="/OurServicesPage" className="inline-flex items-center gap-1.5 font-mono text-xs text-primary hover:gap-2.5 transition-all">
                    <span className="text-secondary">Learn more</span>
                    <span>&rarr;</span>
                  </Link>
                </div>

                <span className="absolute bottom-0 left-0 h-px w-0 group-hover:w-full transition-all duration-500 ease-out" style={{
              backgroundColor: "var(--primary)"
            }} aria-hidden="true" />
              </div>;
        })}
        </div>

        <div className="mt-12 flex justify-center lg:hidden">
          <Button asChild variant="outline" size="lg" className="font-mono text-sm">
            <Link to="/OurServicesPage">
              View all services &rarr;
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}